// Runtime log store keeps sink registration and queued browser events off the public API.
// Separate browser bundles register sinks here after the core asks for them.
import type { LentyLogEvent, LentyLogSink } from '@lentystyle/guard/shared'

export type RuntimeLogLayer = 'critical' | 'optional'

type RuntimeLogState = {
  sinks: Partial<Record<RuntimeLogLayer, LentyLogSink>>
  queues: Record<RuntimeLogLayer, LentyLogEvent[]>
}

const RUNTIME_LOG_STATE_KEY = '__LentyStyleRuntimeLogState__'

// Register one browser sink for the requested runtime log layer.
export function registerRuntimeLogSink(layer: RuntimeLogLayer, sink: LentyLogSink): void {
  const state = getRuntimeLogState()
  state.sinks[layer] = sink
  flushRuntimeLogQueue(layer)
}

// Check whether the requested runtime log layer already has a sink.
export function hasRuntimeLogSink(layer: RuntimeLogLayer): boolean {
  return Boolean(getRuntimeLogState().sinks[layer])
}

// Deliver one event immediately when a sink exists, otherwise queue it.
export function enqueueRuntimeLog(layer: RuntimeLogLayer, event: LentyLogEvent): void {
  const state = getRuntimeLogState()
  const sink = state.sinks[layer]
  if (sink) {
    sink(event)
    return
  }

  state.queues[layer].push(event)
}

// Flush the queued events for one runtime log layer into its registered sink.
export function flushRuntimeLogQueue(layer: RuntimeLogLayer): void {
  const state = getRuntimeLogState()
  const sink = state.sinks[layer]
  if (!sink) {
    return
  }

  const queue = state.queues[layer]
  while (queue.length > 0) {
    sink(queue.shift() as LentyLogEvent)
  }
}

// Drain queued events so callers can fall back when a bundle fails to load.
export function drainRuntimeLogQueue(layer: RuntimeLogLayer): LentyLogEvent[] {
  const queue = getRuntimeLogState().queues[layer]
  const drained = queue.slice()
  queue.length = 0
  return drained
}

// Resolve one shared browser runtime log store from the global object.
function getRuntimeLogState(): RuntimeLogState {
  const runtimeGlobal = globalThis as typeof globalThis & {
    [RUNTIME_LOG_STATE_KEY]?: RuntimeLogState
  }
  if (runtimeGlobal[RUNTIME_LOG_STATE_KEY]) {
    return runtimeGlobal[RUNTIME_LOG_STATE_KEY] as RuntimeLogState
  }

  const nextState: RuntimeLogState = {
    sinks: {},
    queues: {
      critical: [],
      optional: [],
    },
  }
  runtimeGlobal[RUNTIME_LOG_STATE_KEY] = nextState
  return nextState
}
